import { EntityList } from '@/common/entityFramework/entityList/EntityList';
import { GridConstants } from '@/common/grid/GridConstants';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { Box, Button, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * GP-9: This is an optional step in the Calc Job Dialog Wizard for non-reproduction calculations.
 * The user selects the policy used in the calculation
 */
export const PolSelectStep = ({ calcChainReq, setCalcChainReq, setNextStep, setStepReady }) => {

  const rsrc = useRsrc();

  const [allPolsRecords, setAllPolsRecords] = useState({});

  const [currJobIndex, setCurrJobIndex] = useState(0);

  const [subtitle, setSubtitle] = useState();

  const jobReqsList = calcChainReq.calcJobReqs;
  const liabPolType = calcChainReq.liabPolType;

  const currJobReq = useMemo(() => jobReqsList[currJobIndex], [jobReqsList, currJobIndex]);

  const allPols = allPolsRecords[currJobIndex] || false;


  useEffect(() => {
    const allSelected = jobReqsList.every(job => !!job.polId);
    const nextPage = calcChainReq.entity === 'REALLOC_CALC' ? 'Options' : 'Products';

    setNextStep(allSelected ? nextPage : null);
  }, [jobReqsList]);


  const entityListRef = useRef();
  const clientSideRowData = useCallback((setDataSource) => {
    if (setStepReady) setStepReady(false);
    const rowsReq = {};

    if (allPols) {
      rowsReq.expr = ExprUtils.whereParams('type', liabPolType.code);
    } else {
      rowsReq.replaceParams = {
        periodCode: currJobReq.period.code,
        polType: liabPolType.code
      }
    }

    const rsrcCall = allPols
      ? rsrc.rows('gp', 'POL', rowsReq)
      : rsrc.rows('gp', 'POL_SEL', rowsReq);

    rsrcCall.then((rows) => {
      setDataSource(rows);
      if (setStepReady) setStepReady(true);

      const gridApi = entityListRef.current?.gridRef.current?.gridApiRef.current;

      if (rows.length === 1) {
        const polId = rows[0].id;
        setCalcChainReq(prev => {
          const updated = [...prev.calcJobReqs];
          updated[currJobIndex] = {
            ...updated[currJobIndex],
            polId: polId
          };
          return { ...prev, calcJobReqs: updated };
        });

        if (!gridApi) return;
        gridApi.getDisplayedRowAtIndex(0).setSelected(true);
        return;
      }

      const selectedPol = currJobReq.polId;
      if (!selectedPol) return;

      gridApi.forEachNode((node) => {
        if (selectedPol === node.data?.id) {
          node.setSelected(true);
        }
      });
    });
  }, [currJobIndex, allPols]);


  const augFieldDefs = (fieldToFieldDef) => {
    const columns = ['code', 'name', 'startPeriodId', 'endPeriodId', 'recBy'];

    Object.keys(fieldToFieldDef).forEach(colName => {
      if (!columns.includes(colName)) {
        delete fieldToFieldDef[colName];
      }
    });

    fieldToFieldDef.checkbox = GridConstants.checkbox;
  };

  const rowSelected = (event) => {
    const selectedRows = event.api.getSelectedRows();
    const selectedPolId = selectedRows.length === 1 ? selectedRows[0].id : null;

    setCalcChainReq(prev => {
      const updated = [...prev.calcJobReqs];
      updated[currJobIndex] = {
        ...updated[currJobIndex],
        polId: selectedPolId
      };

      return { ...prev, calcJobReqs: updated };
    });
  };

  const handleToggle = (e, value) => {

    const newVal = value === 'all';
    setAllPolsRecords(prev => ({
      ...prev,
      [currJobIndex]: newVal,
    }));

    // Reset selected policy on toggle change
    setCalcChainReq(prev => {
      const updated = [...prev.calcJobReqs];
      updated[currJobIndex] = {
        ...updated[currJobIndex],
        polId: null
      };
      return { ...prev, calcJobReqs: updated };
    });
  };

  useEffect(() => {
    const currJobReq = jobReqsList?.[currJobIndex];
    const currPeriod = currJobReq?.period.code;

    const nCalcs = jobReqsList.length;
    if (nCalcs === 1) {
      setSubtitle(`Select a policy to calculate ${liabPolType.code} for period ${currPeriod}.`);
    } else {
      setSubtitle(`Select a policy to calculate ${liabPolType.code} for period ${currPeriod} (${currJobIndex + 1} of ${nCalcs}).`);
    }
  }, [currJobIndex]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          my: 1
        }}>
        <Typography variant="h6">{subtitle}</Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* All Policies Toggle */}
          <ToggleButtonGroup
            value={allPols ? 'all' : 'active'}
            exclusive
            onChange={handleToggle}
            sx={{ height: 40, color: 'primary.main' }}>
            <ToggleButton value="active" data-test-id="toggle-active" sx={{ whiteSpace: 'nowrap' }}>
              Active Policy
            </ToggleButton>
            <ToggleButton value="all" data-test-id="toggle-all" sx={{ whiteSpace: 'nowrap' }}>
              All Policies
            </ToggleButton>
          </ToggleButtonGroup>

          {jobReqsList.length > 1 &&
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setCurrJobIndex(currJobIndex - 1)}
                disabled={currJobIndex === 0}
                sx={{ whiteSpace: 'nowrap' }}>
                Previous Calculation
              </Button>
              <Button
                variant="outlined"
                onClick={() => setCurrJobIndex(currJobIndex + 1)}
                disabled={currJobIndex === jobReqsList.length - 1 || !currJobReq?.polId}
                sx={{ whiteSpace: 'nowrap' }}>
                Next Calculation
              </Button>
            </Box>}
        </Box>
      </Box>

      {/* Grid */}
      <Box sx={{ flex: 1, minHeight: 10, overflow: 'auto', width: '100%' }}>
        <EntityList
          key={`${currJobIndex}-${allPols}`}
          ref={entityListRef}
          entity="POL"
          clientSideRowData={clientSideRowData}
          augFieldDefs={augFieldDefs}
          events={{ onRowSelected: rowSelected }}
          gridOptions={{
            rowSelection: 'single',
          }}
        />
      </Box>
    </Box >
  );
};
